import React, { useState, useEffect } from 'react';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Button } from '../../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { CustomDatePicker } from '../../ui/custom-date-picker';
import { CLIENT_OPTIONS } from '../constants';
import { Lock, Loader2, Sparkles, FileText, Eye, Download, Mail, CheckCircle, X } from 'lucide-react';
import { AutocompleteInput } from '../../ui/autocomplete-input';
import { toast } from 'sonner';
import { getTodayString } from '../../../utils/dateUtils';

const SVE_API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/api\/?$/, '')}/api`;

const SVE_LOAD_TYPE_OPTIONS = ['General', 'Addition/Deduction'];

// Component for Addition/Deduction summary calculation with invoice generation
function AdditionDeductionSummary({ compact = false, formData, handleFieldChange, summaryInvoiceFile: propFile, setSummaryInvoiceFile: propSetFile, isEditMode = false, peekedNumber, peeking, peekNextInvoiceNumber }) {
  // Local state fallback when props not provided
  const [localFile, setLocalFile] = React.useState(null);
  const [uploading, setUploading] = React.useState(false);
  const summaryInvoiceFile = propFile !== undefined ? propFile : localFile;
  const setSummaryInvoiceFile = propSetFile || setLocalFile;
  // Manual invoice-number override is available to super users only
  const canUseManualInvoice = React.useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}').role === 'super_approver'; }
    catch { return false; }
  }, []);
  // Track whether we have saved data from an existing load (edit mode)
  const hasSavedData = isEditMode && formData?.summary_net_amount !== undefined && formData?.summary_net_amount !== '' && formData?.summary_net_amount !== 0;
  // Track if user has manually changed dates (to trigger re-fetch in edit mode)
  const [userChangedDates, setUserChangedDates] = useState(false);
  
  const [fromDate, setFromDate] = useState(formData?.summary_from_date || '');
  const [toDate, setToDate] = useState(formData?.summary_to_date || '');
  const [loading, setLoading] = useState(false);
  const [summaryData, setSummaryData] = useState(
    // If editing and we have saved data, use it
    hasSavedData ? {
      total_additions: formData.summary_total_additions || 0,
      total_deductions: formData.summary_total_deductions || 0,
      total_addition_tons: formData.summary_total_addition_tons || 0,
      total_deduction_tons: formData.summary_total_deduction_tons || 0
    } : null
  );
  const [error, setError] = useState('');
  const [generating, setGenerating] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [generatedInvoice, setGeneratedInvoice] = useState(formData?.summary_generated_invoice_id ? {
    invoice_id: formData.summary_generated_invoice_id,
    invoice_number: formData.summary_generated_invoice_number,
    total_amount: formData.summary_invoice_amount
  } : null);
  
  const today = getTodayString();
  
  // Calculate net values - use formData if available, else calculate from summaryData
  const netAmount = formData?.summary_net_amount !== undefined && formData?.summary_net_amount !== '' 
    ? Number(formData.summary_net_amount) 
    : (summaryData ? (summaryData.total_additions - summaryData.total_deductions) : null);
  const netTons = formData?.summary_net_tons !== undefined && formData?.summary_net_tons !== '' 
    ? Number(formData.summary_net_tons) 
    : (summaryData ? (summaryData.total_addition_tons - summaryData.total_deduction_tons) : null);
  const isPositive = netAmount !== null && netAmount > 0;
  
  // Custom date change handlers that track user interaction
  const handleFromDateChange = (date) => {
    setFromDate(date || '');
    if (isEditMode) setUserChangedDates(true);
  };
  
  const handleToDateChange = (date) => {
    setToDate(date || '');
    if (isEditMode) setUserChangedDates(true);
  };
  
  useEffect(() => {
    if (fromDate && toDate) {
      // In edit mode with saved data, only fetch if user explicitly changed dates
      if (hasSavedData && !userChangedDates) {
        // Don't re-fetch - use saved data. Just store dates in formData.
        if (handleFieldChange) {
          handleFieldChange('summary_from_date', fromDate);
          handleFieldChange('summary_to_date', toDate);
        }
        return;
      }
      fetchSummary();
      // Store dates in form data
      if (handleFieldChange) {
        handleFieldChange('summary_from_date', fromDate);
        handleFieldChange('summary_to_date', toDate);
      }
    } else {
      setSummaryData(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate]);
  
  // Update form data when summary changes (only when fetched fresh, not from saved data)
  useEffect(() => {
    if (summaryData && handleFieldChange && (!hasSavedData || userChangedDates)) {
      const calculatedNetAmount = summaryData.total_additions - summaryData.total_deductions;
      const calculatedNetTons = summaryData.total_addition_tons - summaryData.total_deduction_tons;
      handleFieldChange('summary_net_amount', calculatedNetAmount);
      handleFieldChange('summary_net_tons', calculatedNetTons);
      handleFieldChange('summary_total_additions', summaryData.total_additions);
      handleFieldChange('summary_total_deductions', summaryData.total_deductions);
      handleFieldChange('summary_total_addition_tons', summaryData.total_addition_tons);
      handleFieldChange('summary_total_deduction_tons', summaryData.total_deduction_tons);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summaryData]);
  
  const fetchSummary = async () => {
    setLoading(true);
    setError('');
    const url = `${SVE_API}/silica-loads/sve-summary?from_date=${fromDate}&to_date=${toDate}`;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const text = await response.text().catch(() => '');
        let detail = '';
        try { detail = JSON.parse(text)?.detail; } catch(e) {}
        const detailStr = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(e => e.msg || JSON.stringify(e)).join('; ') : '';
        setError({
          operation: 'Fetch SVE Summary',
          method: 'GET',
          url,
          params: { from_date: fromDate, to_date: toDate },
          status: response.status,
          statusText: response.statusText,
          detail: detailStr,
          rawBody: text?.slice(0, 300) || '(empty)',
        });
        toast.error(`Fetch SVE Summary failed — HTTP ${response.status}`, {
          description: `URL: ${url}\nDetail: ${detailStr || text?.slice(0, 120) || '(empty)'}`,
          duration: 15000,
        });
        return;
      }
      
      const data = await response.json();
      setSummaryData(data);
    } catch (err) {
      setError({
        operation: 'Fetch SVE Summary',
        method: 'GET',
        url,
        params: { from_date: fromDate, to_date: toDate },
        status: 'Network Error',
        detail: err.message,
        rawBody: '',
      });
      toast.error(`Fetch SVE Summary — Network Error`, {
        description: `URL: ${url}\nError: ${err.message}`,
        duration: 15000,
      });
      console.error('SVE Summary fetch error:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleGenerateInvoice = async () => {
    if (!fromDate || !toDate) {
      setError('Please select both From Date and To Date');
      return;
    }
    
    setGenerating(true);
    setError('');
    const genUrl = `${SVE_API}/silica-loads/generate-sve-summary-invoice`;
    
    try {
      const formDataObj = new FormData();
      formDataObj.append('from_date', fromDate);
      formDataObj.append('to_date', toDate);
      formDataObj.append('include_stamp', 'true');
      if (canUseManualInvoice && formData?.manual_summary_invoice_number) {
        formDataObj.append('manual_invoice_number', formData.manual_summary_invoice_number);
      }
      
      const response = await fetch(genUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formDataObj
      });
      
      if (!response.ok) {
        const text = await response.text().catch(() => '');
        let detail = '';
        try { const parsed = JSON.parse(text); detail = parsed?.detail; } catch(e) {}
        const detailStr = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join('; ') : '';
        setError({
          operation: 'Generate SVE Summary Invoice',
          method: 'POST',
          url: genUrl,
          params: { from_date: fromDate, to_date: toDate, include_stamp: 'true' },
          status: response.status,
          statusText: response.statusText,
          detail: detailStr,
          rawBody: text?.slice(0, 300) || '(empty)',
        });
        toast.error(`Generate Invoice failed — HTTP ${response.status}`, {
          description: `URL: ${genUrl}\nDetail: ${detailStr || text?.slice(0, 120) || '(empty)'}`,
          duration: 15000,
        });
        setGenerating(false);
        return;
      }
      
      const data = await response.json();
      
      // Store generated invoice info
      setGeneratedInvoice({
        invoice_id: data.invoice_id,
        invoice_number: data.invoice_number,
        total_amount: data.total_amount,
        r2_key: data.r2_key
      });
      
      // Update form data with generated invoice info
      if (handleFieldChange) {
        handleFieldChange('summary_generated_invoice_id', data.invoice_id);
        handleFieldChange('summary_generated_invoice_number', data.invoice_number);
        handleFieldChange('summary_invoice_amount', data.total_amount);
        handleFieldChange('summary_r2_key', data.r2_key);
      }
      
      toast.success(`Invoice ${data.invoice_number} generated successfully!`);
      
      // Open the generated PDF in a new tab using fetch+Blob (auth header required)
      try {
        const token = localStorage.getItem('token');
        const viewResp = await fetch(`${SVE_API}/silica-loads/sve-summary-invoice/${data.invoice_id}/view`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (viewResp.ok) {
          const blob = await viewResp.blob();
          window.open(window.URL.createObjectURL(blob), '_blank');
        }
      } catch (viewErr) {
        console.error('Failed to open invoice:', viewErr);
      }
      
    } catch (err) {
      setError({
        operation: 'Generate SVE Summary Invoice',
        method: 'POST',
        url: genUrl,
        params: { from_date: fromDate, to_date: toDate },
        status: 'Network Error',
        detail: err.message,
        rawBody: '',
      });
      toast.error(`Generate Invoice — Network Error`, {
        description: `URL: ${genUrl}\nError: ${err.message}`,
        duration: 15000,
      });
      console.error(err);
    } finally {
      setGenerating(false);
    }
  };
  
  const handleViewInvoice = async () => {
    if (!generatedInvoice?.invoice_id) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${SVE_API}/silica-loads/sve-summary-invoice/${generatedInvoice.invoice_id}/view`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error('Failed to load invoice');
      const blob = await response.blob();
      window.open(window.URL.createObjectURL(blob), '_blank');
    } catch (err) {
      console.error('View invoice error:', err);
    }
  };
  
  const handleDownloadInvoice = async () => {
    if (!generatedInvoice?.invoice_id) return;
    try {
      const response = await fetch(
        `${SVE_API}/silica-loads/sve-summary-invoice/${generatedInvoice.invoice_id}/download`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${generatedInvoice.invoice_number?.replace(/\//g, '_') || 'SVE_Summary'}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Invoice downloaded!');
    } catch (err) {
      toast.error('Failed to download invoice');
      console.error(err);
    }
  };
  
  const handleSendEmail = async () => {
    if (!generatedInvoice?.invoice_id) return;
    setSendingEmail(true);
    try {
      const response = await fetch(
        `${SVE_API}/silica-loads/sve-summary-invoice/${generatedInvoice.invoice_id}/send-email`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        const errorMsg = typeof errorData.detail === 'string' 
          ? errorData.detail 
          : Array.isArray(errorData.detail) 
            ? errorData.detail.map(e => e.msg || e.message).join(', ')
            : 'Failed to send email';
        throw new Error(errorMsg);
      }
      
      toast.success('Invoice sent to SVE Castings!');
    } catch (err) {
      toast.error(err.message || 'Failed to send email');
      console.error(err);
    } finally {
      setSendingEmail(false);
    }
  };
  
  const handleRemoveInvoice = async () => {
    if (!generatedInvoice?.invoice_id) return;
    
    try {
      // Call backend to delete the invoice from R2 and database
      const response = await fetch(
        `${SVE_API}/silica-loads/sve-summary-invoice/${generatedInvoice.invoice_id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        const errorMsg = typeof errorData.detail === 'string' 
          ? errorData.detail 
          : Array.isArray(errorData.detail) 
            ? errorData.detail.map(e => e.msg || e.message).join(', ')
            : 'Failed to remove invoice';
        throw new Error(errorMsg);
      }
      
      // Clear local state
      setGeneratedInvoice(null);
      
      // Clear form data
      if (handleFieldChange) {
        handleFieldChange('summary_generated_invoice_id', '');
        handleFieldChange('summary_generated_invoice_number', '');
        handleFieldChange('summary_invoice_amount', '');
        handleFieldChange('summary_r2_key', '');
      }
      
      toast.success('Invoice removed successfully');
    } catch (err) {
      toast.error(err.message || 'Failed to remove invoice');
      console.error(err);
    }
  };
  
  return (
    <div className="space-y-4 mt-4">
      {/* Date Range Selection */}
      <div className={`grid ${compact ? 'grid-cols-1 gap-3' : 'grid-cols-2 gap-4'}`}>
        <div className="space-y-1">
          <Label className="text-xs text-slate-500 ml-1">From Date *</Label>
          <CustomDatePicker 
            data-testid="summary-from-date"
            value={fromDate} 
            onChange={handleFromDateChange} 
            max={today}
            placeholder="Select from date" 
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-slate-500 ml-1">To Date *</Label>
          <CustomDatePicker 
            data-testid="summary-to-date"
            value={toDate} 
            onChange={handleToDateChange} 
            min={fromDate}
            max={today}
            placeholder="Select to date" 
          />
        </div>
      </div>
      
      {loading && (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-5 h-5 animate-spin text-slate-500" />
          <span className="ml-2 text-sm text-slate-500">Calculating...</span>
        </div>
      )}
      
      {error && (
        typeof error === 'string' ? (
          <p className="text-sm text-red-500">{error}</p>
        ) : (
          <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-xs font-mono space-y-1" data-testid="sve-error-detail">
            <p className="font-semibold text-red-700 text-sm">{error.operation} Failed — HTTP {error.status} {error.statusText || ''}</p>
            <p><span className="text-slate-500">Method:</span> <span className="text-red-600">{error.method}</span></p>
            <p className="break-all"><span className="text-slate-500">URL:</span> <span className="text-red-600">{error.url}</span></p>
            <p><span className="text-slate-500">Params:</span> <span className="text-red-600">{JSON.stringify(error.params)}</span></p>
            {error.detail && <p><span className="text-slate-500">Detail:</span> <span className="text-red-600">{error.detail}</span></p>}
            {error.rawBody && <p className="break-all"><span className="text-slate-500">Raw Response:</span> <span className="text-red-600">{error.rawBody}</span></p>}
          </div>
        )
      )}
      
      {/* Summary Results */}
      {summaryData && !loading && (
        <div className="space-y-3">
          {/* Net Amount Display */}
          <div className={`p-4 rounded-xl ${isPositive ? 'bg-green-50 border border-green-200' : netAmount === 0 ? 'bg-slate-50 border border-slate-200' : 'bg-red-50 border border-red-200'}`}>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-slate-600">Net Amount</Label>
                <p className={`text-sm font-semibold ${isPositive ? 'text-green-600' : netAmount === 0 ? 'text-slate-600' : 'text-red-600'}`}>
                  ₹{Math.abs(netAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  {netAmount < 0 && <span className="text-xs ml-1">(Deduction)</span>}
                  {netAmount === 0 && <span className="text-xs ml-1">(No adjustments)</span>}
                </p>
              </div>
              <div>
                <Label className="text-xs text-slate-600">Net Tons</Label>
                <p className={`text-sm font-semibold ${(netTons || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {Math.abs(netTons || 0).toFixed(3)} T
                </p>
              </div>
            </div>
            <div className="mt-2 pt-2 border-t border-slate-200 grid grid-cols-2 gap-2 text-xs text-slate-500">
              <span>Additions: ₹{(summaryData.total_additions || 0).toLocaleString('en-IN')} | {(summaryData.total_addition_tons || 0).toFixed(3)}T</span>
              <span>Deductions: ₹{(summaryData.total_deductions || 0).toLocaleString('en-IN')} | {(summaryData.total_deduction_tons || 0).toFixed(3)}T</span>
            </div>
          </div>
          
          {/* Generate Invoice Section - Only if net amount is positive */}
          {isPositive && (
            <div className="p-4 bg-white border border-slate-200 rounded-xl space-y-4">
              {/* Generate Invoice Button */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-800">Generate Summary Invoice</p>
                  <p className="text-xs text-slate-500">Net Amount: ₹{netAmount.toLocaleString('en-IN')} | Net Tons: {netTons.toFixed(3)}T</p>
                </div>
                <div className="flex items-center gap-2">
                  {!formData.summary_generated_invoice_number && peekNextInvoiceNumber && (
                    <Button
                      type="button"
                      onClick={peekNextInvoiceNumber}
                      disabled={peeking}
                      className="h-10 px-3 bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300 text-xs font-medium"
                      data-testid="peek-next-number-summary-btn"
                    >
                      {peeking ? '...' : peekedNumber ? `Next: ${peekedNumber}` : 'Next #?'}
                    </Button>
                  )}
                  <Button 
                  type="button" 
                  onClick={handleGenerateInvoice} 
                  disabled={generating}
                  data-testid="generate-summary-invoice-btn"
                  className="bg-indigo-600 hover:bg-indigo-700 text-white h-10 px-5 disabled:opacity-50"
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate Invoice
                    </>
                  )}
                </Button>
                </div>
              </div>

              {/* Manual Invoice Number - only for vijay */}
              {canUseManualInvoice && (
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500 ml-1">Manual Invoice No. (optional)</Label>
                  <Input
                    value={formData?.manual_summary_invoice_number ?? ''}
                    onChange={(e) => handleFieldChange && handleFieldChange('manual_summary_invoice_number', e.target.value)}
                    placeholder="e.g. MJ/SS26050"
                    className="h-10 rounded-xl text-sm"
                    data-testid="manual-summary-invoice-number"
                  />
                </div>
              )}
              
              {/* Generated Invoice Display - Shown as Attachment */}
              {generatedInvoice && (
                <div className="p-4 bg-emerald-50 border border-emerald-300 rounded-xl space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-emerald-100 rounded-lg">
                        <FileText className="w-6 h-6 text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-emerald-800">{generatedInvoice.invoice_number || 'Generated Invoice'}</p>
                        <p className="text-xs text-emerald-600">
                          Amount: ₹{generatedInvoice.total_amount?.toLocaleString('en-IN') || netAmount?.toLocaleString('en-IN')}
                        </p>
                      </div>
                    </div>
                    <button 
                      type="button"
                      onClick={handleRemoveInvoice}
                      className="p-1.5 bg-red-100 hover:bg-red-200 text-red-600 rounded-full transition-colors"
                      title="Remove Invoice"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex items-center gap-2 pt-2 border-t border-emerald-200">
                    <button 
                      type="button"
                      onClick={handleViewInvoice}
                      className="flex-1 px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white text-xs rounded-lg flex items-center justify-center gap-1"
                    >
                      <Eye className="w-3 h-3" />
                      View
                    </button>
                    <button 
                      type="button"
                      onClick={handleDownloadInvoice}
                      className="flex-1 px-3 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-xs rounded-lg flex items-center justify-center gap-1"
                    >
                      <Download className="w-3 h-3" />
                      Download
                    </button>
                    <button 
                      type="button"
                      onClick={handleSendEmail}
                      disabled={sendingEmail}
                      className="flex-1 px-3 py-2 bg-orange-500 hover:bg-orange-600 text-white text-xs rounded-lg flex items-center justify-center gap-1 disabled:opacity-50"
                    >
                      {sendingEmail ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Mail className="w-3 h-3" />
                      )}
                      Email
                    </button>
                  </div>
                  
                  <div className="flex items-center gap-2 text-xs text-emerald-700">
                    <CheckCircle className="w-4 h-4" />
                    <span>Invoice attached and ready for submission</span>
                  </div>
                </div>
              )}
              
              {/* Upload Section - Only show if no generated invoice */}
              {!generatedInvoice && (
                <div className="space-y-1">
                  <Label className="text-xs font-semibold text-slate-700">Or Upload Invoice Manually</Label>
                  {!summaryInvoiceFile ? (
                    <>
                      <label className={`flex flex-col items-center justify-center h-16 rounded-xl border-2 border-dashed cursor-pointer transition-all ${uploading ? 'bg-slate-50 border-slate-300' : 'bg-amber-50 border-amber-300 hover:border-amber-400'}`}>
                        <input 
                          type="file" 
                          accept=".pdf,.jpg,.jpeg,.png" 
                          disabled={uploading}
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            setSummaryInvoiceFile(file);
                            setUploading(true);
                            try {
                              const fd = new FormData();
                              fd.append('file', file);
                              fd.append('invoice_number', formData?.manual_summary_invoice_number || '');
                              fd.append('from_date', fromDate || '');
                              fd.append('to_date', toDate || '');
                              fd.append('net_amount', netAmount || 0);
                              fd.append('total_amount', netAmount || 0);
                              const resp = await fetch(`${SVE_API}/silica-loads/upload-sve-summary-invoice`, {
                                method: 'POST',
                                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                                body: fd
                              });
                              if (!resp.ok) { const err = await resp.json(); throw new Error(err.detail || 'Upload failed'); }
                              const data = await resp.json();
                              setGeneratedInvoice({ invoice_id: data.invoice_id, invoice_number: data.invoice_number, total_amount: data.total_amount });
                              if (handleFieldChange) {
                                handleFieldChange('summary_generated_invoice_id', data.invoice_id);
                                handleFieldChange('summary_generated_invoice_number', data.invoice_number);
                                handleFieldChange('summary_invoice_amount', data.total_amount);
                                handleFieldChange('summary_r2_key', data.r2_key);
                              }
                            } catch (err) {
                              setError(err.message || 'Upload failed');
                              setSummaryInvoiceFile(null);
                            } finally {
                              setUploading(false);
                            }
                          }} 
                          className="hidden" 
                        />
                        {uploading ? <Loader2 className="w-5 h-5 text-slate-400 animate-spin" /> : <FileText className="w-5 h-5 text-amber-500" />}
                        <span className="text-[10px] mt-1 text-amber-600">{uploading ? 'Uploading...' : 'Upload Invoice (if not generating)'}</span>
                      </label>
                    </>
                  ) : (
                    <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-300 rounded-lg">
                      <FileText className="w-5 h-5 text-emerald-600" />
                      <span className="text-sm text-emerald-700 flex-1">{summaryInvoiceFile.name}</span>
                      <button 
                        type="button"
                        onClick={() => setSummaryInvoiceFile(null)}
                        className="p-1 bg-red-100 hover:bg-red-200 text-red-600 rounded-full"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* Message for negative net amount */}
          {!isPositive && netAmount !== null && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
              <p className="text-xs text-amber-700">
                Net amount is negative (more deductions than additions). Invoice generation is not available.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function BasicInfoContent({ formData, handleFieldChange, isEditMode, compact = false, locked = false, onError = null, peekedNumber, peeking, peekNextInvoiceNumber }) {
  const inputH = compact ? 'h-10 rounded-xl text-sm' : 'h-10 rounded-xl';
  const lockedStyle = locked ? 'bg-slate-100 cursor-not-allowed opacity-70' : '';
  
  const isBalaji = formData.client_name === 'BALAJI CASTINGS PVT LTD';
  const isSVE = formData.client_name === 'SVE CASTINGS PVT LTD';
  const sveLoadType = formData.sve_load_type || '';
  
  // Show other fields if: BALAJI selected, OR SVE with "General" selected
  const showOtherFields = isBalaji || (isSVE && sveLoadType === 'General');
  
  if (locked) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-slate-500 text-sm mb-2">
          <Lock className="w-4 h-4" />
          <span>This section is locked</span>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-slate-500 ml-1">Client *</Label>
          <Input value={formData.client_name} readOnly className={`${inputH} ${lockedStyle} ${compact ? '' : 'max-w-xs'}`} />
        </div>
        {isSVE && (
          <div className="space-y-1">
            <Label className="text-xs text-slate-500 ml-1">Load Type *</Label>
            <Input value={formData.sve_load_type || ''} readOnly className={`${inputH} ${lockedStyle} ${compact ? '' : 'max-w-xs'}`} />
          </div>
        )}
        {/* SVE Addition/Deduction locked view - show summary data */}
        {isSVE && sveLoadType === 'Addition/Deduction' && (
          <div className="space-y-3">
            <div className={`grid ${compact ? 'grid-cols-1 gap-2' : 'grid-cols-2 gap-3'}`}>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500 ml-1">From Date *</Label>
                <CustomDatePicker value={formData.summary_from_date} disabled placeholder="From date" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500 ml-1">To Date *</Label>
                <CustomDatePicker value={formData.summary_to_date} disabled placeholder="To date" />
              </div>
            </div>
            {/* Net Amount Summary */}
            {formData.summary_net_amount !== undefined && formData.summary_net_amount !== '' && (
              <div className={`p-4 rounded-xl ${Number(formData.summary_net_amount) > 0 ? 'bg-green-50 border border-green-200' : Number(formData.summary_net_amount) === 0 ? 'bg-slate-50 border border-slate-200' : 'bg-red-50 border border-red-200'}`}>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-slate-600">Net Amount (Final Invoice)</Label>
                    <p className={`text-2xl font-bold ${Number(formData.summary_net_amount) > 0 ? 'text-green-600' : 'text-slate-600'}`}>
                      ₹{Math.abs(Number(formData.summary_net_amount) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600">Net Tons (Total Tons)</Label>
                    <p className={`text-2xl font-bold ${Number(formData.summary_net_tons || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {Math.abs(Number(formData.summary_net_tons || 0)).toFixed(3)} T
                    </p>
                  </div>
                </div>
                <div className="mt-2 pt-2 border-t border-slate-200 grid grid-cols-2 gap-2 text-xs text-slate-500">
                  <span>Additions: ₹{(formData.summary_total_additions || 0).toLocaleString('en-IN')} | {Number(formData.summary_total_addition_tons || 0).toFixed(3)}T</span>
                  <span>Deductions: ₹{(formData.summary_total_deductions || 0).toLocaleString('en-IN')} | {Number(formData.summary_total_deduction_tons || 0).toFixed(3)}T</span>
                </div>
              </div>
            )}
            {/* Generated Invoice Info */}
            {formData.summary_generated_invoice_id && (
              <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                <FileText className="w-5 h-5 text-emerald-600" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-emerald-800">{formData.summary_generated_invoice_number || 'Generated Invoice'}</p>
                  <p className="text-xs text-emerald-600">Amount: ₹{Number(formData.summary_invoice_amount || 0).toLocaleString('en-IN')}</p>
                </div>
              </div>
            )}
          </div>
        )}
        {showOtherFields && (
          <>
            <div className={`grid ${compact ? 'grid-cols-1 gap-2' : 'grid-cols-2 gap-3'}`}>
              <Input value={formData.material_from} readOnly placeholder="Material From *" className={`${inputH} ${lockedStyle}`} />
              <Input value={formData.purchase_order} readOnly placeholder="Purchase Order *" className={`${inputH} ${lockedStyle}`} />
            </div>
            <div className={`grid ${compact ? 'grid-cols-1 gap-2' : 'grid-cols-2 gap-3'}`}>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500 ml-1">Load Date *</Label>
                <CustomDatePicker value={formData.date} disabled placeholder="Select date" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500 ml-1">Date Sent *</Label>
                <CustomDatePicker value={formData.date_sent} disabled placeholder="Select date" />
              </div>
            </div>
            <div className={`grid ${compact ? 'grid-cols-1 gap-2' : 'grid-cols-2 gap-3'}`}>
              <Input value={formData.vehicle_number} readOnly placeholder="Vehicle Number *" className={`${inputH} ${lockedStyle}`} />
              <Input value={formData.transport_company} readOnly placeholder="Transport Company *" className={`${inputH} ${lockedStyle}`} />
            </div>
            <div className={`grid ${compact ? 'grid-cols-1 gap-2' : 'grid-cols-2 gap-3'}`}>
              <Input value={formData.driver_name} readOnly placeholder="Driver Name *" className={`${inputH} ${lockedStyle}`} />
              <Input value={formData.driver_contact} readOnly placeholder="Driver Phone *" className={`${inputH} ${lockedStyle}`} />
            </div>
          </>
        )}
      </div>
    );
  }
  
  return (
    <div className="space-y-3">
      {/* Client Selection - Always shown first */}
      <div className="space-y-1">
        <Label className="text-xs text-slate-500 ml-1">Client *</Label>
        <Select
          value={formData.client_name}
          onValueChange={(v) => {
            if (v === '__ADHOC__') {
              // Adhoc flow is handled by a separate isolated dialog. Fire a
              // page-level event; SilicaLoads.js closes this dialog + opens
              // AddEditAdhocLoadDialog. We intentionally do NOT set client_name.
              window.dispatchEvent(new CustomEvent('open-adhoc-load-dialog'));
              return;
            }
            handleFieldChange('client_name', v);
          }}
          disabled={isEditMode}
        >
          <SelectTrigger data-testid="client-select" className={`${inputH} ${compact ? '' : 'max-w-xs'} ${formData.client_name ? 'border-slate-900' : ''}`}><SelectValue placeholder="Select Client" /></SelectTrigger>
          <SelectContent>
            {CLIENT_OPTIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            {!isEditMode && (
              <SelectItem key="__ADHOC__" value="__ADHOC__" data-testid="client-select-adhoc-option">
                + Adhoc Client (one-off)
              </SelectItem>
            )}
          </SelectContent>
        </Select>
      </div>
      
      {/* SVE Load Type Dropdown - Only shown when SVE is selected */}
      {isSVE && (
        <div className="space-y-1">
          <Label className="text-xs text-slate-500 ml-1">Load Type *</Label>
          <Select value={sveLoadType} onValueChange={(v) => handleFieldChange('sve_load_type', v)} disabled={isEditMode}>
            <SelectTrigger data-testid="sve-load-type-select" className={`${inputH} ${compact ? '' : 'max-w-xs'} ${sveLoadType ? 'border-slate-900' : ''}`}><SelectValue placeholder="Select Load Type" /></SelectTrigger>
            <SelectContent>
              {SVE_LOAD_TYPE_OPTIONS.map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
      
      {/* Addition/Deduction Summary - shown when SVE with Addition/Deduction selected */}
      {isSVE && sveLoadType === 'Addition/Deduction' && (
        <AdditionDeductionSummary 
          compact={compact} 
          formData={formData} 
          handleFieldChange={handleFieldChange}
          isEditMode={isEditMode}
          peekedNumber={peekedNumber} peeking={peeking} peekNextInvoiceNumber={peekNextInvoiceNumber}
        />
      )}
      
      {/* Other fields - shown for BALAJI or SVE with General selected */}
      {showOtherFields && (
        <>
          <div className={`grid ${compact ? 'grid-cols-1 gap-2' : 'grid-cols-2 gap-3'}`}>
            <Input data-testid="material-from" value={formData.material_from} onChange={(e) => handleFieldChange('material_from', e.target.value)} placeholder="Material From *" required className={`${inputH} ${formData.material_from ? 'border-slate-900' : ''}`} />
            <Input data-testid="purchase-order" value={formData.purchase_order} onChange={(e) => handleFieldChange('purchase_order', e.target.value)} placeholder="Purchase Order *" className={`${inputH} ${formData.purchase_order ? 'border-slate-900' : ''}`} />
          </div>
          <div className={`grid ${compact ? 'grid-cols-1 gap-2' : 'grid-cols-2 gap-3'}`}>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500 ml-1">Load Date *</Label>
              <CustomDatePicker data-testid="load-date" value={formData.date} onChange={(date) => handleFieldChange('date', date || '')} placeholder="Select date" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500 ml-1">Date Sent *</Label>
              <CustomDatePicker data-testid="date-sent" value={formData.date_sent} onChange={(date) => handleFieldChange('date_sent', date || '')} placeholder="Select date" />
            </div>
          </div>
          <div className={`grid ${compact ? 'grid-cols-1 gap-2' : 'grid-cols-2 gap-3'}`}>
            <Input data-testid="vehicle-number" value={formData.vehicle_number} onChange={(e) => handleFieldChange('vehicle_number', e.target.value.toUpperCase())} placeholder="Vehicle Number *" required className={`${inputH} ${formData.vehicle_number ? 'border-slate-900' : ''}`} />
            <AutocompleteInput testId="transport-company" value={formData.transport_company} onChange={(v) => handleFieldChange('transport_company', v)} fetchUrl="/silica-loads/transport-companies" placeholder="Transport Company *" required className={`${inputH} ${formData.transport_company ? 'border-slate-900' : ''}`} />
          </div>
          <div className={`grid ${compact ? 'grid-cols-1 gap-2' : 'grid-cols-2 gap-3'}`}>
            <Input data-testid="driver-name" value={formData.driver_name} onChange={(e) => handleFieldChange('driver_name', e.target.value)} placeholder="Driver Name *" required className={`${inputH} ${formData.driver_name ? 'border-slate-900' : ''}`} />
            <Input 
              data-testid="driver-contact"
              value={formData.driver_contact} 
              onChange={(e) => handleFieldChange('driver_contact', e.target.value)} 
              placeholder="Driver Phone (10 digits) *" 
              inputMode="numeric" 
              pattern="[0-9]*" 
              required 
              className={`${inputH} ${
                formData.driver_contact 
                  ? (formData.driver_contact.replace(/\D/g, '').length === 10 
                      ? 'border-slate-900' 
                      : 'border-red-500') 
                  : ''
              }`} 
            />
          </div>
        </>
      )}
    </div>
  );
}
